import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { canAddMorePhotos, countPhotosByStepId, nextStep } from '@transpordiamet/shared';
import api from '@/lib/api';
import { sessionAtom } from '@/lib/atoms';
import { addLangParam } from '@/lib/utils';
import type { Session } from '@transpordiamet/shared';

interface UseStepNavigationParams {
  sessionId: string | undefined;
  stepId: string | undefined;
}

/**
 * Hook to handle step navigation logic
 * Consolidates navigation between steps and to success page
 */
export function useStepNavigation({ sessionId, stepId }: UseStepNavigationParams) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSession = useSetAtom(sessionAtom);

  /**
   * After an upload, or Continue when `force`: stay while this step can take
   * more photos; else go to the next step that still needs or accepts photos;
   * else complete the session and go to success. Rejects if POST /complete fails.
   */
  const navigateToNextStep = useCallback(
    async (session: Session, options?: { force?: boolean; photoSaved?: boolean }) => {
      if (!sessionId || !stepId) return;

      const steps = session.configuration.steps;
      const counts = countPhotosByStepId(session.photos);
      const current = steps.find((s) => s.id === stepId);
      if (!current) return;

      if (!options?.force && canAddMorePhotos(current, counts[stepId] ?? 0)) {
        return;
      }

      const next = nextStep(steps, stepId, counts);
      const saved = options?.photoSaved ? { photoSaved: true } : undefined;
      if (next) {
        navigate(addLangParam(`/${sessionId}/steps/${next.id}`, searchParams), {
          preventScrollReset: true,
          state: saved,
        });
        return;
      }

      if (session.state === 'active') {
        const { data } = await api.post<Session>(`/sessions/${sessionId}/complete`, {});
        setSession(data);
      }
      navigate(addLangParam(`/${sessionId}/success`, searchParams), { replace: true });
    },
    [sessionId, stepId, searchParams, navigate, setSession]
  );

  /**
   * Navigate to a specific step by ID
   * Used for redirecting to first incomplete step
   */
  const navigateToStep = useCallback(
    (targetStepId: string, options?: { replace?: boolean }) => {
      if (!sessionId) return;

      navigate(addLangParam(`/${sessionId}/steps/${targetStepId}`, searchParams), {
        replace: options?.replace ?? true,
        preventScrollReset: true,
      });
    },
    [sessionId, searchParams, navigate]
  );

  return {
    navigateToNextStep,
    navigateToStep,
  };
}
